import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { PlusIcon, UserIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import UnifiedTransactionTable from '../shared/UnifiedTransactionTable';
import { createAuditLog } from '../../utils/auditLog';
interface BranchUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  mobileNumber: string;
  role: string;
  designation?: string;
  branchCode?: string;
  settlementAccount?: string;
  status: 'active' | 'inactive';
  firstLoginRequired: boolean;
  createdAt: string;
  createdBy: string;
}
const BranchUsersPage: React.FC = () => {
  const [users, setUsers] = useState<BranchUser[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BranchUser | null>(null);
  const {
    user: currentUser
  } = useAuth();
  useEffect(() => {
    loadUsers();
  }, []);
  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, statusFilter, roleFilter]);
  const loadUsers = () => {
    const storedUsers = localStorage.getItem('branchUsers');
    if (storedUsers) {
      setUsers(JSON.parse(storedUsers));
    }
  };
  const filterUsers = () => {
    let filtered = [...users];
    if (searchQuery) {
      filtered = filtered.filter(user => user.username.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase()) || user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) || user.mobileNumber.includes(searchQuery));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    setFilteredUsers(filtered);
  };
  const handleCreateUser = (userData: any) => {
    const newUser: BranchUser = {
      id: `USER${Date.now()}`,
      ...userData,
      status: 'active',
      firstLoginRequired: true,
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.id || 'system'
    };
    const updatedUsers = [newUser, ...users];
    setUsers(updatedUsers);
    localStorage.setItem('branchUsers', JSON.stringify(updatedUsers));
    createAuditLog({
      userId: newUser.id,
      eventType: 'Create',
      source: 'BO_USER',
      changes: [{
        fieldName: 'User Created',
        oldValue: null,
        newValue: `${newUser.username} (${newUser.email})`
      }]
    });
    setShowCreateModal(false);
  };
  const handleEditUser = (userData: any) => {
    if (!selectedUser) return;
    const updatedUsers = users.map(user => {
      if (user.id === selectedUser.id) {
        const changes: Array<{
          fieldName: string;
          oldValue: string | null;
          newValue: string | null;
        }> = [];
        Object.keys(userData).forEach(key => {
          if (userData[key] !== selectedUser[key as keyof BranchUser] && key !== 'id') {
            changes.push({
              fieldName: key,
              oldValue: String(selectedUser[key as keyof BranchUser] || ''),
              newValue: String(userData[key] || '')
            });
          }
        });
        if (changes.length > 0) {
          createAuditLog({
            userId: selectedUser.id,
            eventType: 'Edit',
            source: 'BO_USER',
            changes
          });
        }
        return {
          ...user,
          ...userData
        };
      }
      return user;
    });
    setUsers(updatedUsers);
    localStorage.setItem('branchUsers', JSON.stringify(updatedUsers));
    setShowEditModal(false);
    setSelectedUser(null);
  };
  const handleStatusChange = (userId: string, newStatus: 'active' | 'inactive') => {
    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        createAuditLog({
          userId: user.id,
          eventType: 'Status Change',
          source: 'BO_USER',
          changes: [{
            fieldName: 'status',
            oldValue: user.status,
            newValue: newStatus
          }]
        });
        return {
          ...user,
          status: newStatus
        };
      }
      return user;
    });
    setUsers(updatedUsers);
    localStorage.setItem('branchUsers', JSON.stringify(updatedUsers));
  };
  const handleViewDetails = (user: BranchUser) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };
  const columns = [{
    id: 'username',
    label: 'Username',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'fullName',
    label: 'Full Name',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'email',
    label: 'Email',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'mobileNumber',
    label: 'Mobile',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'role',
    label: 'Role',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['maker', 'checker', 'admin'],
    format: (value: string) => <Chip label={value} size="small" color={value === 'admin' ? 'error' : value === 'checker' ? 'warning' : 'primary'} data-id="element-2764" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    filterable: true,
    filterType: 'text' as const,
    format: (value: string) => value || '-'
  }, {
    id: 'status',
    label: 'Status',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['active', 'inactive'],
    format: (value: string) => <Chip label={value} size="small" color={value === 'active' ? 'success' : 'default'} data-id="element-2765" />
  }];
  return <Box sx={{
    p: 3
  }} data-id="element-2766">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 3
    }} data-id="element-2767">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }} data-id="element-2768">
          <UserIcon size={28} color="#0052CC" data-id="element-2769" />
          <Typography variant="h4" sx={{
          fontWeight: 700,
          color: '#0052CC'
        }} data-id="element-2770">
            Branch Users Management
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PlusIcon size={20} data-id="element-2772" />} onClick={() => setShowCreateModal(true)} data-id="element-2771">
          Create User
        </Button>
      </Box>

      <UnifiedTransactionTable columns={columns} data={users} onViewDetails={handleViewDetails} title={`Branch Users (${users.length})`} emptyMessage="No users found" data-id="element-2773" />

      <CreateUserModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onSubmit={handleCreateUser} data-id="element-2774" />
      {selectedUser && <EditUserModal open={showEditModal} onClose={() => {
      setShowEditModal(false);
      setSelectedUser(null);
    }} user={selectedUser} onSubmit={handleEditUser} data-id="element-2775" />}
    </Box>;
};
export default BranchUsersPage;